import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import imageCompression from "browser-image-compression";
import {
  FaImage,
  FaPen,
  FaTrashAlt,
  FaClock,
  FaCamera,
  FaSearch,
  FaSortAmountDown,
  FaChevronDown,
} from "react-icons/fa";

import ServicosForm from "../components/ServicosForm";

const API_URL = "https://acesso.cronosagenda.com.br/api";

const ServicosList = () => {
  const [servicos, setServicos] = useState([]);
  const [servicoParaEditar, setServicoParaEditar] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [mensagemSucesso, setMensagemSucesso] = useState("");
  const [atualizar, setAtualizar] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [busca, setBusca] = useState("");
  const [ordenacao, setOrdenacao] = useState("nome");

  const [uploadsEmAndamento, setUploadsEmAndamento] = useState([]);
  const [operacoesEmAndamento, setOperacoesEmAndamento] = useState([]);

  // Controle da lista compacta.
  const [expandido, setExpandido] = useState(false);
  const [limiteInicial, setLimiteInicial] = useState(() =>
    typeof window !== "undefined" && window.innerWidth <= 640 ? 3 : 6
  );

  const navigate = useNavigate();

  // =========================================================
  // AJUSTAR LIMITE CONFORME O TAMANHO DA TELA
  // =========================================================

  useEffect(() => {
    const atualizarLimite = () => {
      const novoLimite = window.innerWidth <= 640 ? 3 : 6;

      setLimiteInicial(novoLimite);
    };

    window.addEventListener("resize", atualizarLimite);

    return () => {
      window.removeEventListener("resize", atualizarLimite);
    };
  }, []);

  // =========================================================
  // BUSCAR SERVIÇOS
  // =========================================================

  const fetchServicos = useCallback(async () => {
    try {
      setCarregando(true);

      await axios.get(`${API_URL}/verificar-autenticacao`, {
        withCredentials: true,
      });

      const response = await axios.get(`${API_URL}/servicos`, {
        withCredentials: true,
      });

      setServicos(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (err) {
      console.error("Erro ao buscar serviços:", err);

      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/login");
      } else {
        setMensagemSucesso("Erro ao carregar os serviços.");
      }
    } finally {
      setCarregando(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchServicos();
  }, [fetchServicos, atualizar]);

  // =========================================================
  // MENSAGENS
  // =========================================================

  useEffect(() => {
    if (!mensagemSucesso) return undefined;

    const timer = setTimeout(() => {
      setMensagemSucesso("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [mensagemSucesso]);

  const exibirMensagem = (mensagem) => {
    setMensagemSucesso(mensagem);
  };

  // =========================================================
  // CONTROLE DE OPERAÇÕES POR SERVIÇO
  // =========================================================

  const iniciarOperacao = (id) => {
    setOperacoesEmAndamento((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  const finalizarOperacao = (id) => {
    setOperacoesEmAndamento((prev) =>
      prev.filter((item) => item !== id)
    );
  };

  const iniciarUpload = (id) => {
    setUploadsEmAndamento((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  const finalizarUpload = (id) => {
    setUploadsEmAndamento((prev) =>
      prev.filter((item) => item !== id)
    );
  };

  // =========================================================
  // EXCLUIR SERVIÇO
  // =========================================================

  const handleDelete = async (id) => {
    const servico = servicos.find((item) => item.id === id);

    const confirmado = window.confirm(
      `Tem certeza que deseja excluir o serviço${
        servico?.nome ? ` "${servico.nome}"` : ""
      }?`
    );

    if (!confirmado) return;

    try {
      iniciarOperacao(id);

      await axios.get(`${API_URL}/verificar-autenticacao`, {
        withCredentials: true,
      });

      await axios.delete(`${API_URL}/servicos/${id}`, {
        withCredentials: true,
      });

      setServicos((prev) =>
        prev.filter((item) => item.id !== id)
      );

      exibirMensagem("Serviço excluído com sucesso!");
    } catch (err) {
      console.error("Erro ao excluir serviço:", err);

      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/login");
      } else {
        exibirMensagem(
          err.response?.data?.error ||
            "Erro ao excluir serviço."
        );
      }
    } finally {
      finalizarOperacao(id);
    }
  };

  // =========================================================
  // ENVIAR FOTO DO SERVIÇO
  // =========================================================

  const handleUploadFoto = async (e, servicoId) => {
    const input = e.target;
    const file = input.files?.[0];

    if (!file) return;

    input.value = "";

    if (!file.type.startsWith("image/")) {
      exibirMensagem("Selecione um arquivo de imagem válido.");
      return;
    }

    const maxBytes = 10 * 1024 * 1024;

    if (file.size > maxBytes) {
      exibirMensagem("A imagem deve ter no máximo 10 MB.");
      return;
    }

    try {
      iniciarUpload(servicoId);

      const compressed = await imageCompression(file, {
        maxSizeMB: 0.3,
        maxWidthOrHeight: 500,
        useWebWorker: true,
      });

      const formData = new FormData();

      formData.append("foto", compressed);
      formData.append("servico_id", servicoId);

      const res = await axios.post(
        `${API_URL}/upload-servico`,
        formData,
        {
          withCredentials: true,
        }
      );

      const novaUrl = res.data?.url;

      if (!novaUrl) {
        throw new Error("O servidor não retornou a URL da imagem.");
      }

      setServicos((prev) =>
        prev.map((servico) =>
          servico.id === servicoId
            ? { ...servico, foto_url: novaUrl }
            : servico
        )
      );

      exibirMensagem("Foto atualizada com sucesso!");
    } catch (err) {
      console.error("Erro ao enviar foto:", err);

      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/login");
      } else {
        exibirMensagem(
          err.response?.data?.error ||
            "Erro ao enviar imagem. Tente novamente."
        );
      }
    } finally {
      finalizarUpload(servicoId);
    }
  };

  // =========================================================
  // REMOVER FOTO
  // =========================================================

  const removerFoto = async (servicoId) => {
    const confirmado = window.confirm(
      "Tem certeza que deseja remover a foto deste serviço?"
    );

    if (!confirmado) return;

    try {
      iniciarOperacao(servicoId);

      await axios.post(
        `${API_URL}/remover-foto-servico`,
        { servico_id: servicoId },
        { withCredentials: true }
      );

      setServicos((prev) =>
        prev.map((servico) =>
          servico.id === servicoId
            ? { ...servico, foto_url: null }
            : servico
        )
      );

      exibirMensagem("Foto removida com sucesso!");
    } catch (err) {
      console.error("Erro ao remover foto:", err);

      if (
        err.response?.status === 401 ||
        err.response?.status === 403
      ) {
        navigate("/login");
      } else {
        exibirMensagem(
          err.response?.data?.error ||
            "Erro ao remover foto."
        );
      }
    } finally {
      finalizarOperacao(servicoId);
    }
  };

  // =========================================================
  // EDITAR SERVIÇO
  // =========================================================

  const handleEditarServico = (servico) => {
    setServicoParaEditar(servico);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setServicoParaEditar(null);
  };

  // =========================================================
  // SALVAR SERVIÇO
  // =========================================================

  const handleSave = (mensagem, servicoSalvo) => {
    exibirMensagem(mensagem);

    if (servicoSalvo) {
      if (servicoSalvo.id) {
        setServicos((prev) => {
          const existe = prev.some(
            (servico) => servico.id === servicoSalvo.id
          );

          if (existe) {
            return prev.map((servico) =>
              servico.id === servicoSalvo.id
                ? { ...servico, ...servicoSalvo }
                : servico
            );
          }

          return [...prev, servicoSalvo];
        });
      } else {
        setAtualizar((prev) => !prev);
      }
    } else {
      setAtualizar((prev) => !prev);
    }
  };

  // =========================================================
  // FORMATAÇÃO DE PREÇO
  // =========================================================

  const formatarPreco = (servico) => {
    const preco = Number.parseFloat(servico.preco);

    if (
      Number.isFinite(preco) &&
      preco === 0 &&
      servico.preco_personalizado
    ) {
      return (
        <span className="servicos-list__price-custom">
          {servico.preco_personalizado}
        </span>
      );
    }

    return (
      <span className="servicos-list__price">
        {Number.isFinite(preco)
          ? preco.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })
          : "Preço não informado"}
      </span>
    );
  };

  // =========================================================
  // BUSCA E ORDENAÇÃO
  // =========================================================

  const servicosFiltrados = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase("pt-BR");

    const resultado = servicos.filter((servico) =>
      (servico.nome || "")
        .toLocaleLowerCase("pt-BR")
        .includes(termo)
    );

    resultado.sort((a, b) => {
      const precoA = Number.parseFloat(a.preco) || 0;
      const precoB = Number.parseFloat(b.preco) || 0;

      if (ordenacao === "preco-menor") {
        return precoA - precoB;
      }

      if (ordenacao === "preco-maior") {
        return precoB - precoA;
      }

      if (ordenacao === "duracao") {
        return (Number(a.duracao) || 0) - (Number(b.duracao) || 0);
      }

      return (a.nome || "").localeCompare(
        b.nome || "",
        "pt-BR",
        { sensitivity: "base" }
      );
    });

    return resultado;
  }, [servicos, busca, ordenacao]);

  // =========================================================
  // EXPANSÃO E RECOLHIMENTO
  // =========================================================

  const exibindoBusca = busca.trim().length > 0;

  const servicosVisiveis = useMemo(() => {
    // Durante a busca, todos os resultados ficam visíveis.
    if (exibindoBusca || expandido) {
      return servicosFiltrados;
    }

    return servicosFiltrados.slice(0, limiteInicial);
  }, [
    servicosFiltrados,
    exibindoBusca,
    expandido,
    limiteInicial,
  ]);

  // Ao limpar a busca, retorna automaticamente à lista compacta.
  useEffect(() => {
    if (exibindoBusca) {
      setExpandido(false);
    }
  }, [exibindoBusca]);

  // =========================================================
  // CARREGAMENTO
  // =========================================================

  if (carregando) {
    return (
      <div className="servicos-list">
        <div className="servicos-list__loading">
          <span className="servicos-list__spinner" />
          <p>Carregando serviços...</p>
        </div>
      </div>
    );
  }

  // =========================================================
  // INTERFACE
  // =========================================================

  return (
    <div className="servicos-list">
      {showForm && (
        <div
          className="servicos-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseForm();
            }
          }}
        >
          <div
            className="servicos-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="servicos-modal-title"
          >
            <div className="servicos-modal__header">
              <div>
                <span className="servicos-modal__eyebrow">
                  GERENCIAMENTO DE SERVIÇOS
                </span>

                <h2 id="servicos-modal-title">
                  {servicoParaEditar
                    ? "Editar serviço"
                    : "Novo serviço"}
                </h2>

                <p>
                  Atualize as informações do serviço abaixo.
                </p>
              </div>

              <button
                type="button"
                className="servicos-modal__close"
                onClick={handleCloseForm}
                aria-label="Fechar formulário"
                title="Fechar"
              >
                ×
              </button>
            </div>

            <div className="servicos-modal__body">
              <ServicosForm
                servicoParaEditar={servicoParaEditar}
                onClose={handleCloseForm}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      )}


      {mensagemSucesso && (
        <div
          className={`servicos-list__toast ${
            mensagemSucesso.toLowerCase().includes("erro") ||
            mensagemSucesso.toLowerCase().includes("não foi")
              ? "servicos-list__toast--error"
              : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {mensagemSucesso}
        </div>
      )}

      {servicos.length > 0 && (
        <div className="servicos-list__toolbar">
          <div className="servicos-list__search">
            <FaSearch aria-hidden="true" />

            <input
              type="search"
              placeholder="Buscar serviço..."
              aria-label="Buscar serviço pelo nome"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            {busca && (
              <button
                type="button"
                onClick={() => setBusca("")}
                aria-label="Limpar busca"
              >
                ×
              </button>
            )}
          </div>

          <label className="servicos-list__sort">
            <FaSortAmountDown aria-hidden="true" />

            <select
              aria-label="Ordenar serviços"
              value={ordenacao}
              onChange={(e) => setOrdenacao(e.target.value)}
            >
              <option value="nome">Nome A–Z</option>
              <option value="preco-menor">Menor preço</option>
              <option value="preco-maior">Maior preço</option>
              <option value="duracao">Menor duração</option>
            </select>
          </label>

          <span className="servicos-list__count">
            {servicosFiltrados.length} de {servicos.length} serviços
          </span>
        </div>
      )}

      {servicos.length === 0 ? (
        <div className="servicos-list__empty">
          <div className="servicos-list__empty-icon">
            <FaImage />
          </div>

          <h3>Nenhum serviço cadastrado</h3>

          <p>
            Os serviços cadastrados aparecerão aqui para você gerenciar.
          </p>
        </div>
      ) : servicosFiltrados.length === 0 ? (
        <div className="servicos-list__empty">
          <div className="servicos-list__empty-icon">
            <FaSearch />
          </div>

          <h3>Nenhum serviço encontrado</h3>

          <p>
            Não encontramos serviços com esse nome. Tente outro termo.
          </p>

          <button
            type="button"
            className="servicos-list__edit-button"
            onClick={() => setBusca("")}
          >
            Limpar busca
          </button>
        </div>
      ) : (
        <>
          <div className="servicos-list__grid">
            {servicosVisiveis.map((servico) => {
              const operacaoEmAndamento =
                operacoesEmAndamento.includes(servico.id);

              const uploadEmAndamento =
                uploadsEmAndamento.includes(servico.id);

              return (
                <article
                  className="servicos-list__card"
                  key={servico.id}
                >
                  <div className="servicos-list__card-main">
                    <div className="servicos-list__photo-wrap">
                      {servico.foto_url ? (
                        <img
                          src={servico.foto_url}
                          alt={servico.nome || "Foto do serviço"}
                          className="servicos-list__photo"
                          loading="lazy"
                        />
                      ) : (
                        <div className="servicos-list__no-photo">
                          <FaImage />
                          <span>Sem foto</span>
                        </div>
                      )}

                      <label
                        className="servicos-list__photo-overlay"
                        title={
                          uploadEmAndamento
                            ? "Enviando imagem..."
                            : "Alterar foto do serviço"
                        }
                        aria-label={`Alterar foto de ${servico.nome}`}
                      >
                        {uploadEmAndamento ? (
                          <span className="servicos-list__spinner" />
                        ) : (
                          <FaCamera />
                        )}

                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) =>
                            handleUploadFoto(e, servico.id)
                          }
                          className="servicos-list__file-input"
                          disabled={uploadEmAndamento}
                          aria-label={`Selecionar foto para ${servico.nome}`}
                        />
                      </label>

                      {servico.foto_url && (
                        <button
                          type="button"
                          onClick={() => removerFoto(servico.id)}
                          className="servicos-list__remove-photo"
                          title="Remover foto"
                          aria-label={`Remover foto de ${servico.nome}`}
                          disabled={operacaoEmAndamento}
                        >
                          ×
                        </button>
                      )}
                    </div>

                    <div className="servicos-list__details">
                      <h3 className="servicos-list__name">
                        {servico.nome}
                      </h3>

                      <div className="servicos-list__metadata">
                        <span className="servicos-list__duration">
                          <FaClock />
                          {servico.duracao || 0} min
                        </span>
                      </div>

                      <div className="servicos-list__price-row">
                        {formatarPreco(servico)}
                      </div>
                    </div>
                  </div>

                  <div className="servicos-list__actions">
                    <button
                      type="button"
                      onClick={() => handleEditarServico(servico)}
                      className="servicos-list__edit-button"
                      title="Editar serviço"
                      aria-label={`Editar ${servico.nome}`}
                      disabled={operacaoEmAndamento}
                    >
                      <FaPen />
                      <span>Editar</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(servico.id)}
                      className="servicos-list__delete-button"
                      title="Excluir serviço"
                      aria-label={`Excluir ${servico.nome}`}
                      disabled={operacaoEmAndamento || uploadEmAndamento}
                    >
                      <FaTrashAlt />
                      <span>
                        {operacaoEmAndamento ? "Aguarde..." : "Excluir"}
                      </span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {!exibindoBusca &&
            servicosFiltrados.length > limiteInicial && (
              <div className="servicos-list__expandir">
                <button
                  type="button"
                  className="servicos-list__expandir-btn"
                  onClick={() =>
                    setExpandido((anterior) => !anterior)
                  }
                  aria-expanded={expandido}
                >
                  <span>
                    {expandido
                      ? "Mostrar menos"
                      : `Ver todos os serviços (${servicosFiltrados.length})`}
                  </span>

                  <FaChevronDown
                    className={`servicos-list__expandir-icone ${
                      expandido ? "is-expandido" : ""
                    }`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            )}

          {exibindoBusca && (
            <p className="servicos-list__count">
              {servicosFiltrados.length} resultado(s) encontrado(s)
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default ServicosList;
